import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { colors, spacing, typography } from '../../core/theme/tokens';
import { useSpendCalendar } from '../../hooks/useSpendCalendar';
import { formatCurrency } from '../charts/d3/format';
import { SpendCalendarHeatmap } from '../charts/SpendCalendarHeatmap';
import { WidgetInsightsSlot } from '../assistant/WidgetInsightCallout';
import { SpendAlertSuggestions } from './SpendAlertSuggestions';
import { ErrorRetry } from '../ui/ErrorRetry';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';
import { SpendCategoryChipRail } from './SpendCategoryChipRail';
import { SpendDayTransactionsModal } from './SpendDayTransactionsModal';
import type { SpendDayModalTarget } from './spendDayModal';

export type SpendCalendarCategory = {
  id: string;
  name: string;
  color: string;
};

type Props = {
  /** Month key `YYYY-MM` or year key `YYYY` — calendar year is derived from this. */
  periodKey: string;
  /** Categories for the filter chips (current period’s spend categories). */
  categories?: SpendCalendarCategory[];
  /** When browsing by month, dim other months in the year grid. */
  highlightMonthKey?: string;
  onDayPress?: (date: string, amount: number) => void;
};

function yearFromPeriodKey(periodKey: string): number {
  const year = Number(periodKey.slice(0, 4));
  return Number.isFinite(year) ? year : new Date().getUTCFullYear();
}

/** Year spend calendar — brighter day cells mean higher spend. */
export function SpendCalendarCard({
  periodKey,
  categories = [],
  highlightMonthKey,
  onDayPress,
}: Props) {
  const year = useMemo(() => yearFromPeriodKey(periodKey), [periodKey]);
  const [categoryId, setCategoryId] = useState<string>('all');
  const [dayTarget, setDayTarget] = useState<SpendDayModalTarget | null>(null);
  const activeCategory = categories.find((c) => c.id === categoryId);
  const { days, totalSpend, loading, error, isLive, requestKey, refresh } = useSpendCalendar({
    year,
    categoryId,
    enabled: true,
  });

  useEffect(() => {
    if (
      categoryId !== 'all' &&
      categories.length > 0 &&
      !categories.some((c) => c.id === categoryId)
    ) {
      setCategoryId('all');
    }
  }, [categories, categoryId]);

  if (!isLive) return null;

  const hasSpend = days.some((d) => d.amount > 0);
  const filterLabel = activeCategory?.name ?? 'All spending';

  const handleDayPress = (date: string, amount: number) => {
    setDayTarget({
      date,
      amount,
      categorySlug: categoryId === 'all' ? undefined : categoryId,
      categoryName: activeCategory?.name,
    });
    onDayPress?.(date, amount);
  };

  return (
    <>
      <GlassCard style={styles.card} accessibilityLabel={`Spend calendar ${year}`}>
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text style={styles.title}>Daily spend</Text>
            <Text style={styles.sub}>
              {loading
                ? `Loading ${filterLabel.toLowerCase()}…`
                : `${filterLabel} · tap a day for details`}
            </Text>
          </View>
          <View style={styles.headerActions}>
            <RefreshButton onPress={refresh} refreshing={loading} compact />
            <View style={styles.kpi}>
              <Text style={styles.kpiValue}>
                {loading ? '—' : formatCurrency(totalSpend)}
              </Text>
              <Text style={styles.kpiLabel}>{year}</Text>
            </View>
          </View>
        </View>

        {categories.length > 0 ? (
          <SpendCategoryChipRail
            categories={categories}
            selectedId={categoryId}
            onSelect={setCategoryId}
          />
        ) : null}

        {error ? (
          <ErrorRetry message={error} onRetry={refresh} refreshing={loading} compact />
        ) : null}

        {loading ? (
          <ActivityIndicator color={colors.accent} style={styles.loader} />
        ) : !hasSpend ? (
          <Text style={styles.empty}>No daily spend for {filterLabel} in {year}.</Text>
        ) : (
          <View style={styles.chartPad}>
            <SpendCalendarHeatmap
              key={requestKey}
              year={year}
              days={days}
              highlightMonthKey={highlightMonthKey}
              onDayPress={handleDayPress}
            />
          </View>
        )}

        <WidgetInsightsSlot widget="spend_calendar" style={styles.insightSlot} />
        <SpendAlertSuggestions
          categorySlug={categoryId}
          categoryLabel={filterLabel}
        />
      </GlassCard>

      <SpendDayTransactionsModal
        target={dayTarget}
        onClose={() => setDayTarget(null)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  headerText: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  sub: {
    ...typography.caption,
    color: colors.textMuted,
  },
  kpi: {
    alignItems: 'flex-end',
    gap: 2,
  },
  kpiValue: {
    ...typography.headline,
    color: colors.text,
    fontSize: 22,
    fontVariant: ['tabular-nums'],
    fontWeight: '700',
  },
  kpiLabel: {
    ...typography.caption,
    color: colors.textMuted,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  chartPad: {
    marginTop: spacing.xs,
  },
  loader: {
    marginVertical: spacing.lg,
  },
  empty: {
    ...typography.caption,
    color: colors.textMuted,
    paddingVertical: spacing.md,
  },
  error: {
    ...typography.caption,
    color: colors.warning,
  },
  retryHint: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '700',
    marginTop: 4,
  },
  insightSlot: {
    marginTop: spacing.sm,
  },
});
